<template>
  <div class="shipment-truck-page">
    <div class="features f-r-vc">
      <el-button
        type="primary"
        class="feature-button deep-green-bg"
        @click="goToSearchShipment(null)"
      >
        <svg-icon
          icon-class="info"
          class="svg-white-color"
          style="font-size: 20px;"
        />
        All Shipment
      </el-button>
    </div>
    <div class="shipment-truck-list" v-loading="loadingShipment">
      <el-tabs v-model="curTab" @tab-click="selectTabChange">
        <el-tab-pane
          v-for="(tab, index) in Tabs"
          :key="'shipment-tab-' + index"
          :label="tab"
          :name="tab"
        />
      </el-tabs>
      <searchable-table
        :table-headers="ShipmentPickUpHeaders"
        :table-data="shipmentPage"
        :search-params="searchForm"
        :search-param-change="searchParamChange"
        :has-actions="true"
        :has-row-expand="true"
      >
        <template v-slot:actions="props">
          <el-button
            size="small"
            type="text"
            @click="editTruck(props.row.no)"
            :disabled="props.row.status === ShipmentDeleted"
          >
            Edit
          </el-button>
          <el-button
            size="small"
            type="text"
            :disabled="!props.row.pickUpInfo?.driverEmail"
            @click="() => openSendEmail(props.row)"
          >
            Email
          </el-button>
        </template>
        <template #[totalPalletCount]="props">
          <span class="pallet-counts bold-oswald">
            {{ props.row.palletCount }}
          </span>
        </template>
        <template v-slot:row-expand="props">
          <div style="margin-right: 100px;">
            <shipment-address-tags
              :addressQuery="{ shipmentIds: [props.row.id] }"
            />
            <pick-up-info
              :editable="false"
              :shipment-no="props.row.no"
              :shipment="props.row"
              :mode="FormModes.LOOSE"
            />
            <common-card title="RII Orders">
              <stock-list
                :showActions="false"
                :stockQuery="{ shipmentId: props.row.id, onlyShipped: true }"
                :showHeaderOps="false"
              />
            </common-card>
          </div>
        </template>
      </searchable-table>
    </div>
    <pick-up-info-edit ref="truckEdit" />
    <send-email ref="sendEmail" />
  </div>
</template>
<script>
import set from 'lodash/set'
import debounce from 'lodash/debounce'
// apis
import { queryShipment } from '@/api/shipment'
// mixins
import { pageJumps } from '@/core/mixin/pages'
// model
import {
  ShipmentPickUpHeaders,
  ShipmentPickUpInfoSearchForm,
  ShipmentDeleted,
  ShipmentShipping,
  ShipmentDelivered,
  ShipmentWorking,
  PickUpEmailTemplate
  // PickUpEmailTemplate
} from '@/core/models/shipment'
// components
import SearchableTable from '@/components/common/SearchableTable.vue'
import StockList from '@/components/shipment/StockList.vue'
import PickUpInfoEdit from '@/components/shipment/PickUpInfoEdit.vue'
import SendEmail from '@/components/user/SendEmail.vue'
import PickUpInfo from '@/components/shipment/PickUpInfo.vue'
import CommonCard from '@/components/common/CommonCard.vue'
import ShipmentAddressTags from '@/components/shipment/ShipmentAddressTags.vue'
// msg
import PagesMessage from '@/core/message/pages'
import {
  DefaultWarpSearchForm,
  InitSearchForm,
  FormModes
} from '@/core/configs/types'
import {
  ShipmentPickUpTabs,
  TabsMixin,
  ThisMonthTab,
  ThisWeekTab,
  TodayTab
} from '@/core/mixin/tabs'
import { getMonth, getToday, getWeek } from '@/core/utils'
import qrcode from 'qrcode'
import { getEmailPassword, sendEmail } from '@/api/mail'
import { encryptData } from '@/api/encrypt'

// tabs
export default {
  name: 'ShipmentTrucks',
  mixins: [pageJumps, TabsMixin],
  components: {
    SearchableTable,
    PickUpInfoEdit,
    SendEmail,
    PickUpInfo,
    StockList,
    CommonCard,
    ShipmentAddressTags
  },
  data() {
    return {
      curTab: TodayTab,
      loadingShipment: false,
      ShipmentPickUpHeaders,
      searchForm: InitSearchForm(
        ShipmentPickUpInfoSearchForm,
        ShipmentPickUpHeaders
      ),
      shipmentPage: null,
      totalPalletCount: 'totalPalletCount',
      debounceFetch: debounce(this.fetchShipments, 300),
      ShipmentDeleted,
      FormModes
    }
  },
  computed: {
    Tabs() {
      return ShipmentPickUpTabs
    },
    queryStatus() {
      if (this.curTab === TodayTab) {
        return {
          timeRange: getToday(),
          statusList: [ShipmentWorking]
        }
      } else if (this.curTab === ThisWeekTab) {
        return {
          timeRange: getWeek(),
          statusList: [ShipmentWorking]
        }
      } else if (this.curTab === ThisMonthTab) {
        return {
          timeRange: getMonth(),
          statusList: [ShipmentWorking]
        }
      } else {
        return {
          statusList: [ShipmentShipping, ShipmentDelivered]
        }
      }
    }
  },
  watch: {
    queryStatus() {
      this.debounceFetch()
    }
  },
  methods: {
    selectTabChange() {
      //
    },
    fetchShipments() {
      this.loadingShipment = true
      const searchForm = {
        ...DefaultWarpSearchForm(this.searchForm),
        ...this.queryStatus
      }
      queryShipment(searchForm).then(res => {
        this.loadingShipment = false
        this.shipmentPage = res
      })
    },
    searchParamChange(key, path, value) {
      set(this.searchForm[key], path, value)
      this.debounceFetch()
    },
    editTruck(shipmentNo) {
      this.$refs.truckEdit.open(shipmentNo)
    },
    async openSendEmail(shipment) {
      if (!shipment.pickUpInfo?.driverEmail) {
        this.$message.warning('no driver email for picking up')
        return
      }
      const qrImage = await qrcode.toDataURL(
        JSON.stringify({
          type: 'Shipment',
          id: this.shipmentId
        }),
        {
          margin: 0,
          width: 100,
          height: 100
        }
      )
      const driverEmail = shipment.pickUpInfo?.driverEmail
      this.$refs.sendEmail.open(
        this.sendMaillCb,
        [driverEmail],
        [],
        shipment.no + ' Pick Up Appointment',
        PickUpEmailTemplate(
          shipment.no,
          shipment.pickUpInfo?.driverName ?? 'no name',
          qrImage
        )
      )
    },
    sendMaillCb(email) {
      return new Promise((resolve, reject) => {
        const pass = getEmailPassword()
        function sendCb(encrypted) {
          return sendEmail({
            ...email,
            senderPassword: encrypted
          })
            .then(res => {
              resolve(res)
            })
            .catch(err => reject(err))
        }
        if (!pass) {
          return this.$prompt(
            'Please input your email password',
            'Email Password',
            {
              confirmButtonText: 'OK',
              cancelButtonText: 'Cancel',
              inputType: 'password',
              inputValue: ''
            }
          )
            .then(({ value }) => {
              return encryptData(value).then(sendCb)
            })
            .catch(() => {})
        } else {
          return sendCb(pass)
        }
      })
    }
  },
  mounted() {
    this.debounceFetch()
    PagesMessage.on(
      PagesMessage.eventMap.REFRESH_SHIPMENT_TRUCK,
      this.debounceFetch
    )
  }
}
</script>
<style lang="scss" scoped>
@import '@/assets/styles/common.scss';

.shipment-truck-page {
  width: 100%;
  overflow-x: hidden;

  @extend .f-c;

  .features {
    @extend .global-margin;
  }

  .shipment-truck-list {
    @extend .global-margin;
    @extend .shadow-level-three;

    border-radius: $home-card-border-radius;
    padding: $home-card-padding-y $home-card-padding-x;
    background-color: white;
  }
}

.pallet-counts {
  font-size: 18px;
  font-weight: bold;
  color: black;
}
</style>
